import React from "react";
import {
  Box,
  Center,
  Flex,
  Heading,
  Link,
  Text,
  VisuallyHidden,
} from "@chakra-ui/react";
import Image from "next/image";
import DiagramImage from "public/assets/snobase-hero-vector.svg?image";
import Logo from "public/assets/snobase-wordmark.min.svg";
import { APP_CONTENT_HEIGHT } from "components/Layout/constants";
import { LANDING_PAGE_URL } from "runtime-config/config";

type Props = {
  children: React.ReactNode;
  heading?: string;
  subheading?: string;
};

export default function LoginLayout({ children, heading, subheading }: Props) {
  return (
    <Center
      minHeight={APP_CONTENT_HEIGHT}
      backgroundColor="gray.50"
      px={{ base: 4, md: 8 }}
      py={{ base: 6, md: 10 }}
    >
      <Flex
        width="100%"
        maxWidth="64rem"
        minHeight={{ base: "auto", md: "35rem" }}
        backgroundColor="white"
        borderRadius="lg"
        boxShadow="0 24px 70px rgba(80, 0, 95, 0.18)"
        overflow="hidden"
        direction={{ base: "column", md: "row" }}
      >
        <Flex
          display={{ base: "none", md: "flex" }}
          width="41.6667%"
          backgroundColor="deepPlum"
          color="white"
          px={10}
          py={10}
          direction="column"
          justifyContent="flex-start"
          overflow="hidden"
        >
          <Box>
            <Link
              href={LANDING_PAGE_URL}
              display="inline-block"
              aria-label="SnoBase home"
            >
              <Box as={Logo} width="8rem" />
            </Link>

            <Heading as="h2" fontSize="3xl" lineHeight={1.1} mt={12} mb={4}>
              The intelligent CRM for the retail channel
            </Heading>

            <Text color="gray.300" fontSize="md" lineHeight={1.7}>
              SnoBase helps brands sell more by connecting everything that goes
              into retail sales, building your personalized knowledge base to
              drive automation and decision-making.
            </Text>
          </Box>

          <Box
            as={Image}
            src={DiagramImage}
            alt="SnoBase connects products, retailers, distributors, submissions, category reviews, brokers, promotions, agents and your knowledge base."
            sizes="(min-width: 48em) 27rem, 0px"
            width="100%"
            height="auto"
            objectFit="contain"
            mt={4}
          />
        </Flex>

        <Flex
          width={{ base: "100%", md: "58.3333%" }}
          px={{ base: 6, sm: 8, md: 12 }}
          py={{ base: 8, md: 12 }}
          alignItems="center"
          justifyContent="center"
        >
          <Box width="100%" maxWidth="28rem">
            <Link
              href={LANDING_PAGE_URL}
              display={{ base: "inline-block", md: "none" }}
              mb={8}
              aria-label="SnoBase home"
            >
              <Box as={Logo} width="8rem" />
            </Link>

            {heading ? (
              <Heading
                as="h1"
                fontSize={{ base: "2xl", md: "3xl" }}
                color="magenta.veryDark"
                lineHeight={1.18}
              >
                {heading}
              </Heading>
            ) : (
              <VisuallyHidden as="h1">SnoBase</VisuallyHidden>
            )}

            {subheading && (
              <Text color="gray.500" fontSize="sm" marginTop={2} mb={8}>
                {subheading}
              </Text>
            )}

            {children}
          </Box>
        </Flex>
      </Flex>
    </Center>
  );
}

export const getLayout = (page: React.ReactElement) => (
  <LoginLayout>{page}</LoginLayout>
);
